import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  createGSTRate,
  getAllGSTRates,
  updateGSTRate,
  deleteGSTRate,
  getActiveGSTRates,
  getGSTConfig,
  updateGSTConfig,
  calculateGST,
  getCurrentRatesForService,
  getGSTRateHistory,
  getGSTSummary,
  bulkCreateGSTRates
} from '../../Services/Operations/ProductAPI';
import '../../Styles/GSTManagement.css';

const SkeletonStatsCard = () => (
  <div className="summary-card skeleton">
    <div className="skeleton-title"></div>
    <div className="skeleton-value"></div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="skeleton-row">
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-actions"></div></td>
  </tr>
);

const SkeletonTable = () => (
  <div className="table-container skeleton">
    <table className="data-table">
      <thead>
        <tr>
          <th>S.No</th>
          <th>Tax Type</th>
          <th>Service Type</th>
          <th>Rate (%)</th>
          <th>HSN/SAC</th>
          <th>Applicable From</th>
          <th>Applicable To</th>
          <th>Priority</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <SkeletonTableRow key={i} />
        ))}
      </tbody>
    </table>
  </div>
);

const SkeletonConfigForm = () => (
  <div className="config-form skeleton">
    <div className="form-section">
      <div className="skeleton-section-title"></div>
      <div className="form-row">
        <div className="form-group"><div className="skeleton-input"></div></div>
        <div className="form-group"><div className="skeleton-input"></div></div>
      </div>
      <div className="form-row">
        <div className="form-group"><div className="skeleton-input"></div></div>
        <div className="form-group"><div className="skeleton-input"></div></div>
      </div>
    </div>
    <div className="form-section">
      <div className="skeleton-section-title"></div>
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="service-rates">
          <div className="skeleton-subtitle"></div>
          <div className="form-row">
            <div className="form-group"><div className="skeleton-checkbox"></div></div>
            <div className="form-group"><div className="skeleton-input"></div></div>
            <div className="form-group"><div className="skeleton-input"></div></div>
            <div className="form-group"><div className="skeleton-input"></div></div>
          </div>
        </div>
      ))}
    </div>
    <div className="form-actions">
      <div className="skeleton-button"></div>
    </div>
  </div>
);

const SkeletonCalculator = () => (
  <div className="calculator-container skeleton">
    <div className="calculator-form">
      <div className="skeleton-title"></div>
      <div className="form-group"><div className="skeleton-input"></div></div>
      <div className="form-group"><div className="skeleton-select"></div></div>
      <div className="form-group"><div className="skeleton-input"></div></div>
      <div className="form-group"><div className="skeleton-input"></div></div>
      <div className="skeleton-button"></div>
    </div>
    <div className="calculator-result">
      <div className="skeleton-title"></div>
      <div className="result-card">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="skeleton-result-row"></div>
        ))}
      </div>
    </div>
  </div>
);

const SkeletonReports = () => (
  <div className="tab-content">
    <div className="summary-cards">
      {[1, 2, 3].map((i) => (
        <SkeletonStatsCard key={i} />
      ))}
    </div>
    <div className="reports-section">
      <div className="skeleton-section-title"></div>
      <div className="current-rates-grid">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="rate-card skeleton">
            <div className="skeleton-subtitle"></div>
            {[1, 2, 3].map((j) => (
              <div key={j} className="rate-item">
                <div className="skeleton-badge"></div>
                <div className="skeleton-cell"></div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
    <div className="reports-section">
      <div className="skeleton-section-title"></div>
      <div className="table-container">
        <div className="skeleton-table-header"></div>
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="skeleton-history-row"></div>
        ))}
      </div>
    </div>
  </div>
);

const SkeletonModal = () => (
  <div className="modal-overlay">
    <div className="modal-content skeleton-modal">
      <div className="modal-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-close"></div>
      </div>
      <div className="modal-body">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="form-group">
            <div className="skeleton-label"></div>
            <div className="skeleton-input"></div>
          </div>
        ))}
      </div>
      <div className="modal-footer">
        <div className="skeleton-button"></div>
        <div className="skeleton-button"></div>
      </div>
    </div>
  </div>
);

const GSTManagement = () => {
  const { token } = useSelector(state => state.user);
  
  const [activeTab, setActiveTab] = useState('rates');
  const [rates, setRates] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [showRateModal, setShowRateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [viewingRate, setViewingRate] = useState(null);
  
  const [rateForm, setRateForm] = useState({
    taxType: 'CGST',
    serviceType: 'ac_bus',
    rate: '',
    hsnSacCode: '9964',
    description: '',
    applicableFrom: new Date().toISOString().split('T')[0],
    applicableTo: '',
    priority: 0
  });
  
  const [bulkRates, setBulkRates] = useState([]);
  const [bulkRatesText, setBulkRatesText] = useState('');
  
  const [calculator, setCalculator] = useState({
    amount: '',
    serviceType: 'ac_bus',
    placeOfSupply: '',
    placeOfConsumption: ''
  });
  const [calculationResult, setCalculationResult] = useState(null);
  
  const [filters, setFilters] = useState({
    taxType: '',
    serviceType: '',
    isActive: 'true'
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  
  const [rateHistory, setRateHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [currentRates, setCurrentRates] = useState({});
  
  const serviceTypes = [
    { value: 'ac_bus', label: 'AC Bus' },
    { value: 'non_ac_bus', label: 'Non-AC Bus' },
    { value: 'luxury_bus', label: 'Luxury Bus' },
    { value: 'sleeper_bus', label: 'Sleeper Bus' }
  ];
  
  const taxTypes = ['CGST', 'SGST', 'IGST'];
  
  useEffect(() => {
    fetchAllData();
  }, [activeTab, filters, pagination.page]);
  
  const fetchAllData = async () => {
    if (activeTab === 'rates') {
      await fetchRates();
    } else if (activeTab === 'config') {
      await fetchConfig();
    } else if (activeTab === 'reports') {
      await fetchReports();
    }
  };
  
  const fetchRates = async () => {
    try {
      setLoading(true);
      const response = await getAllGSTRates( {
        page: pagination.page,
        limit: pagination.limit,
        taxType: filters.taxType,
        serviceType: filters.serviceType,
        isActive: filters.isActive
      },token);
      
      if (response.success) {
        setRates(response.data.rates);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
          pages: response.data.pagination.pages
        }));
      }
    } catch (err) {
      setError('Failed to fetch GST rates');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await getGSTConfig(token);
      if (response.success) {
        setConfig(response.data);
      }
    } catch (err) {
      setError('Failed to fetch GST configuration');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchReports = async () => {
    try {
      setLoading(true);
      const [historyRes, summaryRes, currentRatesRes] = await Promise.all([
        getGSTRateHistory(token),
        getGSTSummary(token),
        getActiveGSTRates(token)
      ]);
      
      if (historyRes.success) setRateHistory(historyRes.data.history);
      if (summaryRes.success) setSummary(summaryRes.data);
      if (currentRatesRes.success) setCurrentRates(currentRatesRes.data.grouped);
    } catch (err) {
      setError('Failed to fetch reports');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateRate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await createGSTRate(rateForm, token);
      if (response.success) {
        setSuccess('GST rate created successfully');
        setShowRateModal(false);
        resetRateForm();
        fetchRates();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create GST rate');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdateRate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await updateGSTRate(editingRate._id, rateForm, token);
      if (response.success) {
        setSuccess('GST rate updated successfully');
        setShowRateModal(false);
        setEditingRate(null);
        resetRateForm();
        fetchRates();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update GST rate');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteRate = async (id) => {
    if (window.confirm('Are you sure you want to deactivate this GST rate?')) {
      try {
        setLoading(true);
        const response = await deleteGSTRate(id, token);
        if (response.success) {
          setSuccess('GST rate deactivated successfully');
          fetchRates();
          setTimeout(() => setSuccess(null), 3000);
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete GST rate');
        setTimeout(() => setError(null), 3000);
      } finally {
        setLoading(false);
      }
    }
  };
  
  const handleEditRate = (rate) => {
    setEditingRate(rate);
    setRateForm({
      taxType: rate.taxType,
      serviceType: rate.serviceType,
      rate: rate.rate,
      hsnSacCode: rate.hsnSacCode,
      description: rate.description || '',
      applicableFrom: rate.applicableFrom?.split('T')[0] || new Date().toISOString().split('T')[0],
      applicableTo: rate.applicableTo?.split('T')[0] || '',
      priority: rate.priority || 0
    });
    setShowRateModal(true);
  };
  
  const handleViewRate = (rate) => {
    setViewingRate(rate);
  };
  
  const handleUpdateConfig = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await updateGSTConfig(config, token);
      if (response.success) {
        setSuccess('GST configuration updated successfully');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update configuration');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCalculateGST = async () => {
    try {
      setLoading(true);
      const response = await calculateGST(calculator, token);
      if (response.success) {
        setCalculationResult(response.data);
      }
    } catch (err) {
      setError('Failed to calculate GST');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };
  
  const handleBulkCreate = async () => {
    try {
      const ratesArray = bulkRatesText.split('\n')
        .filter(line => line.trim())
        .map(line => {
          const [taxType, serviceType, rate, ...rest] = line.split(',');
          return {
            taxType: taxType?.trim(),
            serviceType: serviceType?.trim(),
            rate: parseFloat(rate),
            description: rest.join(',') || `${taxType} ${rate}% for ${serviceType}`
          };
        });
      
      const response = await bulkCreateGSTRates(ratesArray, token);
      if (response.success) {
        setSuccess(`Created ${response.data.created.length} rates successfully`);
        setShowBulkModal(false);
        setBulkRatesText('');
        fetchRates();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError('Failed to bulk create rates');
      setTimeout(() => setError(null), 3000);
    }
  };
  
  const resetRateForm = () => {
    setRateForm({
      taxType: 'CGST',
      serviceType: 'ac_bus',
      rate: '',
      hsnSacCode: '9964',
      description: '',
      applicableFrom: new Date().toISOString().split('T')[0],
      applicableTo: '',
      priority: 0
    });
  };
  
  const handleRateFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRateForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setConfig(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setConfig(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleConfigNestedChange = (category, field, value) => {
    setConfig(prev => ({
      ...prev,
      defaultRates: {
        ...prev.defaultRates,
        [category]: {
          ...prev.defaultRates[category],
          [field]: parseFloat(value) || 0
        }
      }
    }));
  };
  
  const getStatusBadge = (isActive, applicableTo) => {
    if (!isActive) return <span className="status-badge inactive">Inactive</span>;
    if (applicableTo && new Date(applicableTo) < new Date()) {
      return <span className="status-badge expired">Expired</span>;
    }
    return <span className="status-badge active">Active</span>;
  };
  
  const renderLoadingContent = () => {
    switch (activeTab) {
      case 'rates':
        return <SkeletonTable />;
      case 'config':
        return <SkeletonConfigForm />;
      case 'calculator':
        return <SkeletonCalculator />;
      case 'reports':
        return <SkeletonReports />;
      default:
        return <SkeletonTable />;
    }
  };
  
  return (
    <div className="gst-management">
      <div className="page-header">
        <h1>GST Management</h1>
        <div className="header-actions">
          {activeTab === 'rates' && !loading && (
            <>
              <button className="btn-primary" onClick={() => setShowRateModal(true)}>
                + Add GST Rate
              </button>
              <button className="btn-secondary" onClick={() => setShowBulkModal(true)}>
                Bulk Import
              </button>
            </>
          )}
          {activeTab === 'rates' && loading && (
            <>
              <div className="skeleton-button-small"></div>
              <div className="skeleton-button-small"></div>
            </>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}


      <div className="gst-tabs">
        <button className={activeTab === 'rates' ? 'tab active' : 'tab'} onClick={() => setActiveTab('rates')}>
          GST Rates
        </button>
        <button className={activeTab === 'config' ? 'tab active' : 'tab'} onClick={() => setActiveTab('config')}>
          Configuration
        </button>
        <button className={activeTab === 'calculator' ? 'tab active' : 'tab'} onClick={() => setActiveTab('calculator')}>
          GST Calculator
        </button>
        <button className={activeTab === 'reports' ? 'tab active' : 'tab'} onClick={() => setActiveTab('reports')}>
          Reports
        </button>
      </div>


      {loading && activeTab !== 'config' && activeTab !== 'calculator' && renderLoadingContent()}


      {activeTab === 'rates' && !loading && (
        <div className="tab-content">

          <div className="filters-bar">
            <select
              value={filters.taxType}
              onChange={(e) => setFilters({ ...filters, taxType: e.target.value, page: 1 })}
              className="filter-select"
            >
              <option value="">All Tax Types</option>
              {taxTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            
            <select
              value={filters.serviceType}
              onChange={(e) => setFilters({ ...filters, serviceType: e.target.value, page: 1 })}
              className="filter-select"
            >
              <option value="">All Service Types</option>
              {serviceTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            
            <select
              value={filters.isActive}
              onChange={(e) => setFilters({ ...filters, isActive: e.target.value, page: 1 })}
              className="filter-select"
            >
              <option value="true">Active Only</option>
              <option value="false">Inactive Only</option>
              <option value="">All</option>
            </select>
            
            <button className="refresh-btn" onClick={fetchRates}>Refresh</button>
          </div>


          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Tax Type</th>
                  <th>Service Type</th>
                  <th>Rate (%)</th>
                  <th>HSN/SAC</th>
                  <th>Applicable From</th>
                  <th>Applicable To</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rates.length === 0 ? (
                  <tr><td colSpan="10" className="no-data">No GST rates found</td></tr>
                ) : (
                  rates.map((rate, index) => (
                    <tr key={rate._id}>
                      <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                      <td><span className={`tax-badge ${rate.taxType.toLowerCase()}`}>{rate.taxType}</span></td>
                      <td>{serviceTypes.find(s => s.value === rate.serviceType)?.label || rate.serviceType}</td>
                      <td className="rate-value">{rate.rate}%</td>
                      <td>{rate.hsnSacCode}</td>
                      <td>{new Date(rate.applicableFrom).toLocaleDateString()}</td>
                      <td>{rate.applicableTo ? new Date(rate.applicableTo).toLocaleDateString() : 'Ongoing'}</td>
                      <td>{rate.priority}</td>
                      <td>{getStatusBadge(rate.isActive, rate.applicableTo)}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="action-btn view" onClick={() => handleViewRate(rate)}>View</button>
                          <button className="action-btn edit" onClick={() => handleEditRate(rate)}>Edit</button>
                          <button className="action-btn delete" onClick={() => handleDeleteRate(rate._id)}>Delete</button>
                        </div>
                       </td>
                     </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>


          {pagination.pages > 1 && (
            <div className="pagination">
              <button onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })} disabled={pagination.page === 1}>
                Previous
              </button>
              <span>Page {pagination.page} of {pagination.pages}</span>
              <button onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })} disabled={pagination.page === pagination.pages}>
                Next
              </button>
            </div>
          )}
        </div>
      )}


      {activeTab === 'config' && !loading && config && (
        <div className="tab-content">
          <form onSubmit={handleUpdateConfig} className="config-form">
            <div className="form-section">
              <h3>Business Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>GSTIN *</label>
                  <input
                    type="text"
                    name="gstin"
                    value={config.gstin || ''}
                    onChange={handleConfigChange}
                    placeholder="22AAAAA0000A1Z"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Legal Name *</label>
                  <input
                    type="text"
                    name="legalName"
                    value={config.legalName || ''}
                    onChange={handleConfigChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Trade Name</label>
                  <input
                    type="text"
                    name="tradeName"
                    value={config.tradeName || ''}
                    onChange={handleConfigChange}
                  />
                </div>
                <div className="form-group">
                  <label>Financial Year</label>
                  <input
                    type="text"
                    name="financialYear"
                    value={config.financialYear || ''}
                    onChange={handleConfigChange}
                    placeholder="2025-26"
                  />
                </div>
              </div>
              
              <div className="form-section">
                <h4>Address</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Street</label>
                    <input
                      type="text"
                      value={config.address?.street || ''}
                      onChange={(e) => setConfig({ ...config, address: { ...config.address, street: e.target.value } })}
                    />
                  </div>
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      value={config.address?.city || ''}
                      onChange={(e) => setConfig({ ...config, address: { ...config.address, city: e.target.value } })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>State</label>
                    <input
                      type="text"
                      value={config.address?.state || ''}
                      onChange={(e) => setConfig({ ...config, address: { ...config.address, state: e.target.value } })}
                    />
                  </div>
                  <div className="form-group">
                    <label>State Code</label>
                    <input
                      type="text"
                      value={config.address?.stateCode || ''}
                      onChange={(e) => setConfig({ ...config, address: { ...config.address, stateCode: e.target.value } })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Pincode</label>
                    <input
                      type="text"
                      value={config.address?.pincode || ''}
                      onChange={(e) => setConfig({ ...config, address: { ...config.address, pincode: e.target.value } })}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="form-section">
              <h3>Default GST Rates</h3>
              {serviceTypes.map(service => (
                <div key={service.value} className="service-rates">
                  <h4>{service.label}</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Taxable?</label>
                      <input
                        type="checkbox"
                        checked={config.defaultRates?.[service.value]?.isTaxable || false}
                        onChange={(e) => handleConfigNestedChange(service.value, 'isTaxable', e.target.checked)}
                      />
                    </div>
                    <div className="form-group">
                      <label>CGST (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={config.defaultRates?.[service.value]?.cgst || 0}
                        onChange={(e) => handleConfigNestedChange(service.value, 'cgst', e.target.value)}
                        disabled={!config.defaultRates?.[service.value]?.isTaxable}
                      />
                    </div>
                    <div className="form-group">
                      <label>SGST (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={config.defaultRates?.[service.value]?.sgst || 0}
                        onChange={(e) => handleConfigNestedChange(service.value, 'sgst', e.target.value)}
                        disabled={!config.defaultRates?.[service.value]?.isTaxable}
                      />
                    </div>
                    <div className="form-group">
                      <label>IGST (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={config.defaultRates?.[service.value]?.igst || 0}
                        onChange={(e) => handleConfigNestedChange(service.value, 'igst', e.target.value)}
                        disabled={!config.defaultRates?.[service.value]?.isTaxable}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="form-section">
              <h3>Additional Settings</h3>
              <div className="form-row">
                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      name="reverseChargeApplicable"
                      checked={config.reverseChargeApplicable || false}
                      onChange={handleConfigChange}
                    />
                    Reverse Charge Applicable
                  </label>
                </div>
                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      name="isEcommerceOperator"
                      checked={config.isEcommerceOperator || false}
                      onChange={handleConfigChange}
                    />
                    E-commerce Operator
                  </label>
                </div>
              </div>
            </div>
            
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </form>
        </div>
      )}


      {activeTab === 'config' && loading && <SkeletonConfigForm />}


      {activeTab === 'calculator' && !loading && (
        <div className="tab-content">
          <div className="calculator-container">
            <div className="calculator-form">
              <h3>GST Calculator</h3>
              <div className="form-group">
                <label>Amount (₹)</label>
                <input
                  type="number"
                  value={calculator.amount}
                  onChange={(e) => setCalculator({ ...calculator, amount: e.target.value })}
                  placeholder="Enter amount"
                />
              </div>
              
              <div className="form-group">
                <label>Service Type</label>
                <select
                  value={calculator.serviceType}
                  onChange={(e) => setCalculator({ ...calculator, serviceType: e.target.value })}
                >
                  {serviceTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Place of Supply (State Code)</label>
                <input
                  type="text"
                  value={calculator.placeOfSupply}
                  onChange={(e) => setCalculator({ ...calculator, placeOfSupply: e.target.value })}
                  placeholder="e.g., 27 for Maharashtra"
                />
              </div>
              
              <div className="form-group">
                <label>Place of Consumption (State Code)</label>
                <input
                  type="text"
                  value={calculator.placeOfConsumption}
                  onChange={(e) => setCalculator({ ...calculator, placeOfConsumption: e.target.value })}
                  placeholder="e.g., 27 for Maharashtra"
                />
              </div>
              
              <button className="btn-primary" onClick={handleCalculateGST} disabled={loading}>
                Calculate GST
              </button>
            </div>
            
            {calculationResult && (
              <div className="calculator-result">
                <h3>Calculation Result</h3>
                <div className="result-card">
                  <div className="result-row">
                    <span>Taxable Value:</span>
                    <strong>₹{calculationResult.taxableValue?.toFixed(2)}</strong>
                  </div>
                  <div className="result-row">
                    <span>Supply Type:</span>
                    <strong>{calculationResult.supplyType === 'intra-state' ? 'Intra-State (CGST+SGST)' : 'Inter-State (IGST)'}</strong>
                  </div>
                  
                  {calculationResult.cgst && (
                    <>
                      <div className="result-row">
                        <span>CGST ({calculationResult.cgst.rate}%):</span>
                        <strong>₹{calculationResult.cgst.amount?.toFixed(2)}</strong>
                      </div>
                      <div className="result-row">
                        <span>SGST ({calculationResult.sgst.rate}%):</span>
                        <strong>₹{calculationResult.sgst.amount?.toFixed(2)}</strong>
                      </div>
                    </>
                  )}
                  
                  {calculationResult.igst && (
                    <div className="result-row">
                      <span>IGST ({calculationResult.igst.rate}%):</span>
                      <strong>₹{calculationResult.igst.amount?.toFixed(2)}</strong>
                    </div>
                  )}
                  
                  <div className="result-row total">
                    <span>Total GST:</span>
                    <strong>₹{calculationResult.totalGST?.toFixed(2)}</strong>
                  </div>
                  
                  <div className="result-row grand-total">
                    <span>Total Amount (Including GST):</span>
                    <strong>₹{calculationResult.totalAmount?.toFixed(2)}</strong>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}


      {activeTab === 'calculator' && loading && <SkeletonCalculator />}


      {activeTab === 'reports' && !loading && summary && (
        <div className="tab-content">

          <div className="summary-cards">
            <div className="summary-card">
              <h4>Total Active Rates</h4>
              <p className="summary-value">{summary.totalActiveRates}</p>
            </div>
            <div className="summary-card">
              <h4>GSTIN</h4>
              <p className="summary-value">{summary.config?.gstin || 'Not configured'}</p>
            </div>
            <div className="summary-card">
              <h4>Financial Year</h4>
              <p className="summary-value">{summary.config?.financialYear || 'N/A'}</p>
            </div>
          </div>


          <div className="reports-section">
            <h3>Current Active GST Rates</h3>
            <div className="current-rates-grid">
              {Object.entries(currentRates).map(([serviceType, rates]) => (
                <div key={serviceType} className="rate-card">
                  <h4>{serviceTypes.find(s => s.value === serviceType)?.label || serviceType}</h4>
                  {rates.map(rate => (
                    <div key={rate._id} className="rate-item">
                      <span className="rate-tax-type">{rate.taxType}</span>
                      <span className="rate-percentage">{rate.rate}%</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>


          <div className="reports-section">
            <h3>Rate Change History</h3>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Tax Type</th>
                    <th>Service Type</th>
                    <th>Rate</th>
                    <th>Status</th>
                    <th>Created By</th>
                  </tr>
                </thead>
                <tbody>
                  {rateHistory.slice(0, 20).map(history => (
                    <tr key={history._id}>
                      <td>{new Date(history.createdAt).toLocaleDateString()}</td>
                      <td>{history.taxType}</td>
                      <td>{serviceTypes.find(s => s.value === history.serviceType)?.label || history.serviceType}</td>
                      <td className="rate-value">{history.rate}%</td>
                      <td>{getStatusBadge(history.isActive, history.applicableTo)}</td>
                      <td>{history.createdBy?.name || 'System'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}


      {activeTab === 'reports' && loading && <SkeletonReports />}


      {showRateModal && !loading && (
        <div className="modal-overlay" onClick={() => {
          setShowRateModal(false);
          setEditingRate(null);
          resetRateForm();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingRate ? 'Edit GST Rate' : 'Create New GST Rate'}</h3>
              <button className="modal-close" onClick={() => {
                setShowRateModal(false);
                setEditingRate(null);
                resetRateForm();
              }}>×</button>
            </div>
            <form onSubmit={editingRate ? handleUpdateRate : handleCreateRate}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Tax Type *</label>
                  <select name="taxType" value={rateForm.taxType} onChange={handleRateFormChange} required>
                    {taxTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Service Type *</label>
                  <select name="serviceType" value={rateForm.serviceType} onChange={handleRateFormChange} required>
                    {serviceTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Rate (%) *</label>
                  <input
                    type="number"
                    name="rate"
                    step="0.1"
                    value={rateForm.rate}
                    onChange={handleRateFormChange}
                    required
                    placeholder="e.g., 2.5"
                  />
                </div>
                
                <div className="form-group">
                  <label>HSN/SAC Code</label>
                  <input
                    type="text"
                    name="hsnSacCode"
                    value={rateForm.hsnSacCode}
                    onChange={handleRateFormChange}
                    placeholder="9964"
                  />
                </div>
                
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={rateForm.description}
                    onChange={handleRateFormChange}
                    rows="2"
                    placeholder="Optional description"
                  />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Applicable From</label>
                    <input
                      type="date"
                      name="applicableFrom"
                      value={rateForm.applicableFrom}
                      onChange={handleRateFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Applicable To (Optional)</label>
                    <input
                      type="date"
                      name="applicableTo"
                      value={rateForm.applicableTo}
                      onChange={handleRateFormChange}
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Priority (Higher = Higher Priority)</label>
                  <input
                    type="number"
                    name="priority"
                    value={rateForm.priority}
                    onChange={handleRateFormChange}
                    min="0"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => {
                  setShowRateModal(false);
                  setEditingRate(null);
                  resetRateForm();
                }}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : (editingRate ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {showRateModal && loading && <SkeletonModal />}


      {showBulkModal && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Bulk Import GST Rates</h3>
              <button className="modal-close" onClick={() => setShowBulkModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>CSV Format (taxType, serviceType, rate, description)</label>
                <textarea
                  value={bulkRatesText}
                  onChange={(e) => setBulkRatesText(e.target.value)}
                  rows="10"
                  placeholder={`CGST, ac_bus, 2.5, CGST for AC Bus
SGST, ac_bus, 2.5, SGST for AC Bus
IGST, ac_bus, 5, IGST for AC Bus
CGST, luxury_bus, 2.5, CGST for Luxury Bus`}
                  className="bulk-textarea"
                />
                <small>One rate per line, fields separated by commas</small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowBulkModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleBulkCreate}>Import Rates</button>
            </div>
          </div>
        </div>
      )}


      {viewingRate && (
        <div className="modal-overlay" onClick={() => setViewingRate(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>GST Rate Details</h3>
              <button className="modal-close" onClick={() => setViewingRate(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <label>Tax Type:</label>
                <span className={`tax-badge ${viewingRate.taxType.toLowerCase()}`}>{viewingRate.taxType}</span>
              </div>
              <div className="detail-row">
                <label>Service Type:</label>
                <span>{serviceTypes.find(s => s.value === viewingRate.serviceType)?.label || viewingRate.serviceType}</span>
              </div>
              <div className="detail-row">
                <label>Rate:</label>
                <span className="rate-value">{viewingRate.rate}%</span>
              </div>
              <div className="detail-row">
                <label>HSN/SAC Code:</label>
                <span>{viewingRate.hsnSacCode}</span>
              </div>
              <div className="detail-row">
                <label>Description:</label>
                <span>{viewingRate.description || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <label>Applicable From:</label>
                <span>{new Date(viewingRate.applicableFrom).toLocaleDateString()}</span>
              </div>
              <div className="detail-row">
                <label>Applicable To:</label>
                <span>{viewingRate.applicableTo ? new Date(viewingRate.applicableTo).toLocaleDateString() : 'Ongoing'}</span>
              </div>
              <div className="detail-row">
                <label>Priority:</label>
                <span>{viewingRate.priority}</span>
              </div>
              <div className="detail-row">
                <label>Status:</label>
                <span>{getStatusBadge(viewingRate.isActive, viewingRate.applicableTo)}</span>
              </div>
              <div className="detail-row">
                <label>Created:</label>
                <span>{new Date(viewingRate.createdAt).toLocaleString()}</span>
              </div>
              <div className="detail-row">
                <label>Last Updated:</label>
                <span>{new Date(viewingRate.updatedAt).toLocaleString()}</span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setViewingRate(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GSTManagement;